import { getCachedAPIResponse } from "../apiCaching/apiWrapper";
import { apiConfig } from "../config/apiConfig";
import { PreFetchUrls } from "./idleLoader";
import { deregOfServiceWorker } from "./serviceWorkerDeregHelper";

interface CacheSettings {
    timestamp: string;
    enabled: boolean;
    preCache: string[];
    apiCache?: string[];
    enableLogger: boolean;
    fallbackCall: boolean;
    enableAnalytics?: boolean;
}

let cacheSettingsPromise: Promise<CacheSettings | undefined> | null = null;
const removeLocalAndSession:boolean = false;
window.__calledAPIEndpoint = false;

export function clearCacheIfOnline(){
    const displayCacheLog = process.env.SHOW_CACHE_LOG
        ? JSON.parse(process.env.SHOW_CACHE_LOG.toLowerCase())
        : false;
    if (!navigator.onLine) {
        console.log('%c[SW]%c User is offline','background:red; color:#f3f3f3; font-weight: bold; padding: 0 8px', '');
        navigator.serviceWorker?.controller?.postMessage({ origin: location.origin, action: 'offline' });
    } else {
        navigator.serviceWorker?.controller?.postMessage({ origin: location.origin, action: 'online' });
        if (displayCacheLog) console.error("%c[SW]%c Error in fetching service worker config",'background:red; color:#f3f3f3; font-weight: bold; padding: 0 8px', '');
        purge(false);
    }       
}

export function getCacheSettings(): Promise<CacheSettings | undefined> {
    if (!window.__calledAPIEndpoint) {
        cacheSettingsPromise = null;
    }

    if (cacheSettingsPromise) {
        return cacheSettingsPromise;
    }

    if (window.__calledAPIEndpoint) {
        return Promise.resolve(undefined);
    }

    cacheSettingsPromise = loadCacheSettings();
    return cacheSettingsPromise;
}

async function loadCacheSettings(): Promise<CacheSettings | undefined> {
    window.__calledAPIEndpoint = true;
    const displayCacheLog = process.env.SHOW_CACHE_LOG
        ? JSON.parse(process.env.SHOW_CACHE_LOG.toLowerCase())
        : false;
    const url = `${apiConfig.CROWNPEAK_CACHING_SETTINGS}?${Date.now()}`;

    try {
        const cachedResponse = await getCachedAPIResponse(url, 10, false, false) as CacheSettings | undefined;
        if (!cachedResponse || Object.keys(cachedResponse).length === 0) {
            if (displayCacheLog) console.log('%c[SW]%c CP has not set up the Config setting for Caching','background:red; color:#f3f3f3; font-weight: bold; padding: 0 8px', '');
            clearCacheIfOnline();
            return undefined;
        }

        if (displayCacheLog) console.log('%c[SW]%c Cache settings fetched successfully','background:green; color:#f3f3f3; font-weight: bold; padding: 0 8px', '', cachedResponse);
        await checkCacheSettings(cachedResponse);
        return cachedResponse;
    } catch {
        clearCacheIfOnline();
        return undefined;
    }
}

async function checkCacheSettings(response: CacheSettings) {
    const displayCacheLog = process.env.SHOW_CACHE_LOG
        ? JSON.parse(process.env.SHOW_CACHE_LOG.toLowerCase())
        : false;
    const { timestamp, enabled, preCache, apiCache, enableLogger } = response;

    const apiCacheList: string[] = Array.isArray(apiCache) ? apiCache : [];
    if (apiCacheList.length > 0) {
        PreFetchUrls.getInstance().getPreFetchUrls(apiCacheList);
    }

    // Only require timestamp and preCache to proceed; apiCache is optional
    if (!timestamp || !preCache) {
        if (displayCacheLog) console.error("Invalid cache settings response:", response);
        clearCacheIfOnline();
        return;
    }

    // Handle disabled cache state first
    if (!enabled) {
        if (displayCacheLog) console.log('%c[SW]%c Caching is disabled, cleaning up...','background:orange; color:#f3f3f3; font-weight: bold; padding: 0 8px', '');
        localStorage.removeItem("cachingL2");
        
        // Unregister service worker first
        await deregOfServiceWorker();
        
        // Then purge cache
        await purge(enabled);
        return;
    }

    // Handle normal cache operations
    if (await shouldPurgeCache(timestamp)) {
        await purge(enabled);
    }

    const suLogger = enableLogger === true;
    localStorage.setItem("suLogger", String(suLogger));
    localStorage.setItem("fallbackCall", String(false));
    localStorage.setItem("cachingL2", "true");
    
    await updateCache(preCache);
}

async function updateCache(preCache: string[]) {
    const CACHE_NAME = "NBLY-nahsor";
    const cache = await caches.open(CACHE_NAME);
    await addCacheEntry(cache, "offlineCaching", "true");
    if (preCache.length > 0) {
        await addCacheEntry(cache, "preCache", JSON.stringify(preCache));
    } else {
        await removeCacheEntries(cache, "preCache");
    }
}

async function removeCacheEntries(cache: Cache, suffix: string) {
    const normalizedSuffix = `/${suffix}`;  // Ensure normalized key for deletion
    const requests = await cache.keys();
    for (const request of requests) {
        if (new URL(request.url).pathname.endsWith(normalizedSuffix)) {
            await cache.delete(request);
        }
    }
}


async function addCacheEntry(cache: Cache, key: string, value: string) {
    const normalizedKey = `/${key}`;  // Ensure consistent root-based path
    const request = new Request(normalizedKey, { method: 'GET' });  // Always a GET method for consistency
    const response = new Response(value, { headers: { "Content-Type": "text/plain" } });
    await cache.put(request, response);
}

export function setCachePurgeFlag(flag:boolean){
    window.__cachePurgeInProgress = flag;
    
    // Only send message if service worker controller is available
    if (navigator.serviceWorker?.controller) {
        const message = { 
            origin: location.origin, 
            action: flag ? 'cachePurgeInProgress' : 'cachePurgeCompleted'
        };
        
        console.log(`%c[SW]%c Sending message to service worker: ${message.action}`, 'background:purple; color:#fff; font-weight: bold; padding: 0 8px', '');
        navigator.serviceWorker.controller.postMessage(message);
    } else if (flag) {
        // If no controller but we're trying to set the flag to true, log a warning
        console.log('%c[SW]%c Unable to notify service worker - controller not available', 'background:red; color:#fff; font-weight: bold; padding: 0 8px', '');
    }
}

async function purge(enabled = false) {
    const displayCacheLog = process.env.SHOW_CACHE_LOG
        ? JSON.parse(process.env.SHOW_CACHE_LOG.toLowerCase())
        : false;
    
    // Set a flag to indicate purge is in progress
    setCachePurgeFlag(true);
    
    // Store required items before purge
    const requiredItems = extractRequiredItems();

    try {
        // First, unregister the service worker if it exists
        await deregOfServiceWorker();

        // Clear caches using Cache API
        const cacheKeys = await caches.keys();
        await Promise.all(
            cacheKeys.map(key => caches.delete(key))
        );

        if(removeLocalAndSession){
            // Clear localStorage
            localStorage.clear();
            // Clear sessionStorage
            sessionStorage.clear();
        }

        // Clear IndexedDB
        await new Promise((resolve, reject) => {
            const dbRequest = window.indexedDB.deleteDatabase("resource-store");
            dbRequest.onerror = () => {
                if (displayCacheLog) console.error('%c[SW]%c Error deleting IndexedDB database','background:red; color:#f3f3f3; font-weight: bold; padding: 0 8px', '');
                reject(new Error('Failed to delete IndexedDB'));
            };
            dbRequest.onsuccess = () => {
                if (displayCacheLog) console.log('%c[SW]%c IndexedDB database deleted successfully','background:green; color:#f3f3f3; font-weight: bold; padding: 0 8px', '');
                resolve(undefined);
            };
        });

        // Restore required items
        writeToStorage(requiredItems);

        // Set a current timestamp to clear the cache
        localStorage.setItem("lastCacheClear", Date.now().toString());

        // Clear the purge flag
        setCachePurgeFlag(false);

        // Re-register service worker if enabled
        if (process.env.ENABLE_SERVICE_CACHE?.toLowerCase() === 'true' && enabled) {
            try {
                const newRegistration = await navigator.serviceWorker.register('/brand/_assets/js/sw1.js', { scope: '/' });
                if (displayCacheLog) console.log('%c[SW]%c Service worker re-registered','background:green; color:#f3f3f3; font-weight: bold; padding: 0 8px', '');
                
                // Wait for the new service worker to activate
                await new Promise<void>((resolve) => {
                    if (newRegistration.active) {
                        resolve();
                    } else {
                        newRegistration.addEventListener('activate', () => resolve());
                    }
                });
            } catch (error) {
                if (displayCacheLog) console.log('%c[SW]%c Failed to re-register service worker:','background:red; color:#f3f3f3; font-weight: bold; padding: 0 8px', error);
            }
        }

    } catch (error) {
        console.error('Error during purge:', error);
        // Ensure flag is cleared even on error
        setCachePurgeFlag(false);
        throw error;
    }
}

// Add a function to check if purge is needed
async function shouldPurgeCache(timestamp: string): Promise<boolean> {
    try {
        const clearCacheTime = new Date(timestamp).getTime();
        const lastCacheClear = localStorage.getItem("lastCacheClear");
        
        if (!lastCacheClear) {
            return true;
        }

        return Number.isNaN(clearCacheTime) || Number.parseInt(lastCacheClear, 10) < clearCacheTime;
    } catch (error) {
        console.error('Error checking cache purge status:', error);
        return false;
    }
}

// Add TypeScript declaration
declare global {
    interface Window {
        __cachePurgeInProgress?: boolean;
        __calledAPIEndpoint?: boolean;
    }
}

function extractRequiredItems() {
    const requiredLocalItems = [
        "dbaName", "weblocationId", "doingBusinessAs", "franchiseWebLocationId",
        "localPhoneNumber", "sessionIDFr", "cachingL2", "lastCacheClear", "localHeaderUrl",
        "CA", "US", "brandDetails", "dynamicMenuURLResponse", "siteConfigDisplayClickToCallCta"
    ];
    const requiredSessionItems = [
        "doingBusinessAs", "franchiseWebLocationId", "localPhoneNumber",
        "rl-post-data", "NEI-API data", "RL_DBAName", "RL_Weblocation_ID",
        "fR", "sAddressParam", "franchiseId", "fakeSubmitData", "correlationIDFr","rewardConfirmation","insuranceFormPayload"
    ];

    const localStorageItems = requiredLocalItems.reduce((acc, key) => {
        if (localStorage.getItem(key)) acc[key] = localStorage.getItem(key);
        return acc;
    }, {} as Record<string, string | null>);

    const sessionStorageItems = requiredSessionItems.reduce((acc, key) => {
        if (sessionStorage.getItem(key)) acc[key] = sessionStorage.getItem(key);
        return acc;
    }, {} as Record<string, string | null>);

    return { local: localStorageItems, session: sessionStorageItems };
}

function writeToStorage(requiredItems: { local: Record<string, string | null>; session: Record<string, string | null> }) {
    const writeStorage = (storage: Storage, items: Record<string, string | null>) => {
        Object.entries(items).forEach(([key, value]) => {
            if (value !== null) {
                try {
                    const currentValue = storage.getItem(key);
                    if (currentValue !== value) {
                        storage.setItem(key, value);
                    }
                } catch (error) {
                    console.error(`Failed to write key "${key}" to storage`, error);
                }
            }
        });
    };

    if(removeLocalAndSession){
        writeStorage(localStorage, requiredItems.local);
        writeStorage(sessionStorage, requiredItems.session);
    }
}

